import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  Network,
  BarChart3,
  Cloud,
  ClipboardList,
  ExternalLink,
} from "lucide-react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const ResultSection = ({
  title,
  items,
  loading,
  onItemClick,
  linkPrefix = "",
}) => {
  const navigate = useNavigate();

  const handleItemClick = (e, path) => {
    e.preventDefault();
    onItemClick();
    setTimeout(() => {
      navigate(path);
    }, 0);
  };

  return (
    <div className="py-2">
      <div className="px-4 py-2">
        <h3 className="text-xs font-semibold text-[#003366]/70 uppercase tracking-wide">
          {title}
        </h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#003366]/20 border-t-[#003366]" />
        </div>
      ) : items.length > 0 ? (
        <div className="space-y-1">
          {items.map((item, index) => {
            const path = `${linkPrefix}${item.EndpointName || item.name}`;
            return (
              <a
                key={index}
                href={path}
                className="block px-4 py-2 hover:bg-[#003366]/5 transition-all duration-150"
                onClick={(e) => handleItemClick(e, path)}
              >
                <div className="flex items-center group">
                  <IconComponent
                    type={title}
                    className="h-4 w-4 text-[#003366]/60 flex-shrink-0"
                  />
                  <div className="ml-3 flex-1">
                    <p className="text-xs font-medium text-[#003366]">
                      {item.EndpointName || item.name}
                    </p>
                    {/* <p className="text-xs text-[#003366]/60 mt-0.5">
                      Description
                    </p> */}
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-[#003366]/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            );
          })}
        </div>
      ) : (
        <div className="px-4 py-2 text-sm text-[#003366]/40">
          No results found
        </div>
      )}
    </div>
  );
};

const IconComponent = ({ type, className }) => {
  switch (type) {
    case "Endpoints":
      return <Network className={className} />;
    case "Performance Results":
      return <BarChart3 className={className} />;
    case "Audit Results":
      return <ClipboardList className={className} />;
    case "Log Monitor Results":
      return <Cloud className={className} />;
    default:
      return <Network className={className} />;
  }
};

export default function SearchResults({ onClose }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [resultEndpoint, setResultEndpoint] = useState([]);
  const [resultPerformance, setResultPerformance] = useState([]);
  const [resultAudit, setResultAudit] = useState([]);
  const [resultLog, setResultLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessToken = useSelector((state) => state.accessToken.value);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const getDetails = async (search) => {
    setLoading(true);
    const query = `
    query MyQuery($key:String!) {
      getsearchEndpoints(search: $key) {
        EndpointName
      }
    }`;
    const variables = { key: search };

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL,
        { query, variables },
        {
          headers: { Authorization: accessToken },
        }
      );
      setResultEndpoint(response.data.data.getsearchEndpoints);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-16">
      <div
        ref={searchRef}
        className="w-full max-w-2xl mx-4 bg-white rounded-xl overflow-hidden shadow-2xl"
      >
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#003366]/40" />
            <input
              ref={inputRef}
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-[#003366]/5 border-2 border-transparent rounded-lg text-sm text-[#003366] placeholder-[#003366]/40 focus:bg-white focus:border-[#003366] focus:outline-none transition-colors duration-200"
              placeholder="Search endpoints, audits, logs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                getDetails(e.target.value);
              }}
            />
          </div>
        </div>

        {searchTerm && (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-200">
            <ResultSection
              title="Endpoints"
              items={resultEndpoint}
              loading={loading}
              onItemClick={onClose}
              linkPrefix="/endpoint/"
            />
            <ResultSection
              title="Performance"
              items={resultPerformance}
              loading={loading}
              onItemClick={onClose}
            />
            <ResultSection
              title="Security"
              items={resultAudit}
              loading={loading}
              onItemClick={onClose}
            />
            <ResultSection
              title="Log Monitor"
              items={resultLog}
              loading={loading}
              onItemClick={onClose}
            />
          </div>
        )}

        <div className="px-4 py-3 bg-gray-50 text-xs text-[#003366]/60 border-t border-gray-200">
          Press ESC to close
        </div>
      </div>
    </div>
  );
}
